import axios from 'axios';
import { Auth } from 'aws-amplify';
import type { ApiPick, ApiPickListResponse, PicksFilterValues } from './types';

// Hand-typed against the backend at /api/v1/picks. Backend lives in
// backend/src/api/v1/picks. Source of truth for the shape is that file.
//
// URL resolution:
//   1. If REACT_APP_API_BASE_URL is set at build time, use it (override).
//   2. In production builds, default to '' so axios sends relative URLs.
//      The deployed app's CloudFront distribution routes /api/* to the
//      backend Lambda — same origin, no CORS.
//   3. In local dev (CRA dev server), default to http://localhost:4001
//      where `npm run start` in /backend listens.
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ??
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:4001');

const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10_000,
});

// Pull the active Cognito ID token (sub claim, group membership) on every
// request. Falls back silently if no session exists — the request will hit
// the API and get a 401, which the page can surface to the user / kick to
// the Authenticator sign-in flow.
client.interceptors.request.use(async (config) => {
  try {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    config.headers = config.headers ?? {};
    // axios v0.27 headers are AxiosRequestHeaders | undefined — assignment
    // is safe here once we've defaulted.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (config.headers as any).Authorization = `Bearer ${token}`;
  } catch {
    // No active session — let the request go through unauthenticated. The
    // server will respond 401 and React UI can prompt sign-in.
  }
  return config;
});

const DEFAULT_BATCH_SIZE = 25;

export type ListPicksParams = PicksFilterValues & {
  batchSize?: number;
  pageNum?: number;
};

const buildListQuery = (params: ListPicksParams): Record<string, string | number> => {
  const query: Record<string, string | number> = {
    sport: params.sport,
    batchSize: params.batchSize ?? DEFAULT_BATCH_SIZE,
    pageNum: params.pageNum ?? 1,
  };
  if (params.athleteId) query['athlete-id'] = params.athleteId;
  if (params.riskLevel) query['risk-level'] = params.riskLevel;
  if (params.outcome) query.outcome = params.outcome;
  if (params.computedDate) {
    if (params.computedDate === 'today') {
      const todaysDate = new Date();
      todaysDate.setDate(todaysDate.getDate() - 0);
      const todayString = todaysDate.toISOString();
      query['computed-date'] = todayString.slice(0, todayString.indexOf('T'));
    } else if (params.computedDate === 'yesterday') {
      const yesterdayDate = new Date();
      yesterdayDate.setDate(yesterdayDate.getDate() - 1);
      const yesterdayString = yesterdayDate.toISOString();
      query['computed-date'] = yesterdayString.slice(0, yesterdayString.indexOf('T'));
    }
  }
  return query;
};

export const listPicks = async (params: ListPicksParams): Promise<ApiPickListResponse> => {
  const res = await client.get<ApiPickListResponse>('/api/v1/picks', {
    params: buildListQuery(params),
  });
  return res.data;
};

/**
 * Same filtering/pagination as listPicks, scoped to favorite = true
 * picks — no date scoping, no count limit. Backed by
 * /api/v1/picks/favorites — see backend/src/api/v1/picks/index.ts's
 * getFavorites.
 */
export const getFavorites = async (params: ListPicksParams): Promise<ApiPickListResponse> => {
  const res = await client.get<ApiPickListResponse>('/api/v1/picks/favorites', {
    params: buildListQuery(params),
  });
  return res.data;
};

export const getPickById = async (pickId: string): Promise<ApiPick> => {
  const res = await client.get<ApiPick>(`/api/v1/picks/${pickId}`);
  return res.data;
};

/**
 * Returns the top-3 highest-pH lock picks scheduled within the next 24h,
 * each with every leg at >=6/7 last-7 hits. Backed by
 * /api/v1/picks/pick-of-day — see backend/src/api/v1/picks/index.ts.
 *
 * The Lambda contract was widened from a single ApiPick to ApiPick[] —
 * during a Lambda deploy or when a warm container is still serving the
 * old code, this endpoint can return either shape. Normalize here so
 * every caller sees an array regardless of which side of the deploy the
 * Lambda is on.
 */
export const getPickOfDay = async (): Promise<ApiPick[]> => {
  const res = await client.get<ApiPick | ApiPick[]>(`/api/v1/picks/pick-of-day`);
  if (Array.isArray(res.data)) return res.data;
  return res.data ? [res.data] : [];
};

/**
 * Admin-curated companion to pick-of-day — picks flagged via
 * PUT /api/v1/picks/:pickId/our-bet, scoped to today. See
 * backend/src/api/v1/picks/index.ts's getOurBet.
 */
export const getOurBet = async (): Promise<ApiPick[]> => {
  const res = await client.get<ApiPick[]>(`/api/v1/picks/our-bet`);
  return res.data;
};

/** Admin-only: flag (or unflag) a pick as today's "Our Bet". */
export const setOurBet = async (pickId: string, ourBet: boolean): Promise<ApiPick> => {
  const res = await client.put<ApiPick>(`/api/v1/picks/${pickId}/our-bet`, { ourBet });
  return res.data;
};

/** Admin-only: flag (or unflag) a pick as a favorite. */
export const setAsFavorite = async (pickId: string, favorite: boolean): Promise<ApiPick> => {
  const res = await client.put<ApiPick>(`/api/v1/picks/${pickId}/favorite`, { favorite });
  return res.data;
};

export type ApiLockStats = {
  sport: string;
  windowDays: number;
  hits: number;
  misses: number;
  voids: number;
  /** Null when no legs have resolved yet in the window. */
  hitRate: number | null;
};

/**
 * Headline lock-tier success rate over the rolling 42-day window. Powers
 * the dial on the home page's LockPick component.
 */
export const getLockStats = async (sport: string): Promise<ApiLockStats> => {
  const res = await client.get<ApiLockStats>('/api/v1/picks/lock-stats', {
    params: { sport },
  });
  return res.data;
};

/**
 * Same shape as getLockStats, scoped to lock-tier picks also flagged
 * our_bet = true. Powers the LockPick dial on the Our Bet page.
 */
export const getOurBetStats = async (sport: string): Promise<ApiLockStats> => {
  const res = await client.get<ApiLockStats>('/api/v1/picks/our-bet-stats', {
    params: { sport },
  });
  return res.data;
};
